import { pluralize } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';

import {
  useFeatureFlags,
  useKaiProApiClient,
  useScan,
  useScanEditingContextSlice,
  useScanExcelExport,
  useScanPdfExport,
} from '@kaiizen/kai-pro-api-react-state';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { CancelIcon } from '@/Client/Icons';
import { getApprovedParentChangeOrderScanId, logError, triggerDownload } from '@/Client/Lib';
import { useConfirmationModal } from '@/Client/State/ConfirmationModalContext';
import { ReportType } from '@kaiizen/kai-reports';
import {
  useNavigateToChangeOrder,
  useNavigateToCloseoutScopePreview,
  useNavigateToProject,
  useNavigateToPunchlistScopePreview,
  useNavigateToScan,
  useNavigateToScope,
} from '../../State/kaiNavigation';
import { COLORS2, LAYOUT2 } from '../../Styles';
import { Button, DocumentDownloadIcon, Dropdown } from '../../Widgets';
import { useScopePrintPreviewContext } from './ScopePrintPreviewContext';
import { useScopeReportDownloader } from './useScopeReportDownloader';

const TOOLBAR_STYLE = css`
  label: TOOLBAR_STYLE;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: ${LAYOUT2.spacing1}px ${LAYOUT2.spacing2}px;
  background: ${COLORS2.alwaysDark.background.dark};
  border-bottom: 1px solid ${COLORS2.alwaysDark.border.darkGray};
  gap: 12px;
  flex-shrink: 0;

  @media print {
    display: none;
  }
`;

const TOOLBAR_LEFT_SECTION_STYLE = css`
  label: TOOLBAR_LEFT_SECTION_STYLE;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const TOOLBAR_RIGHT_SECTION_STYLE = css`
  label: TOOLBAR_RIGHT_SECTION_STYLE;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const documentNameByReportType: Record<ReportType, string> = {
  fullScope: 'Scope',
  closeout: 'Closeout',
  changeOrder: 'Change Order',
  punchlist: 'Punch List',
};

export const ScopeToolbar = () => {
  const { featureFlags } = useFeatureFlags();
  const {
    baselineScanId,
    baselineScanRoot,
    effectiveScanType,
    projectIsEditable,
    scan,
    scanHasScopeChanges,
    scanHasTasksNeedingRework,
    scanIsReadyForCloseout,
    scanIsReviewable,
    scanIsEditable,
    scanIsSubmittable,
    scanMutators,
    scanRoot,
  } = useScanEditingContextSlice([
    'baselineScanId',
    'baselineScanRoot',
    'effectiveScanType',
    'projectIsEditable',
    'scan',
    'scanHasScopeChanges',
    'scanHasTasksNeedingRework',
    'scanIsReadyForCloseout',
    'scanIsReviewable',
    'scanIsEditable',
    'scanIsSubmittable',
    'scanMutators',
    'scanRoot',
  ]);
  const navigateToProject = useNavigateToProject();
  const navigateToScan = useNavigateToScan();
  const navigateToScope = useNavigateToScope();
  const navigateToChangeOrder = useNavigateToChangeOrder();
  const navigateToPunchlistScopePreview = useNavigateToPunchlistScopePreview();
  const navigateToCloseoutScopePreview = useNavigateToCloseoutScopePreview();
  const confirm = useConfirmationModal();
  const { scanScopeState } = useScanEditingContextSlice(['scanScopeState']);
  const { reportType } = useScopePrintPreviewContext();
  const kaiProApiClient = useKaiProApiClient();
  const isApproved = scan?.scanLifecycleStatus === 'approved';
  const { scan: baselineScan } = useScan(baselineScanId);

  const { prepareReport: prepareExcelReport, isLoading: isExportingExcel } = useScopeReportDownloader({
    exportHook: useScanExcelExport,
    scanId: scan?.id,
    reportType,
    kind: 'Excel',
    eventName: ANALYTICS_EVENTS.scanExportedExcel,
  });

  const { prepareReport: preparePdfReport, isLoading: isExportingPdf } = useScopeReportDownloader({
    exportHook: useScanPdfExport,
    scanId: scan?.id,
    reportType,
    kind: 'PDF',
    eventName: ANALYTICS_EVENTS.scanExportedPdf,
  });

  const documentName = documentNameByReportType[reportType];
  const approvedParentChangeOrderScanId = getApprovedParentChangeOrderScanId(scan, baselineScan);
  const viewChangeOrderScanId = scan?.scanType === 'changeRequest' ? scan.id : approvedParentChangeOrderScanId;

  const downloadScopePhotosZip = async (scanId: string) => {
    try {
      const { token } = await kaiProApiClient.generateReadOnlyToken();
      const url = kaiProApiClient.getScopeReportPhotosZipUrl(scanId, token);
      triggerDownload(url);
      trackEvent(ANALYTICS_EVENTS.scanExportedPhotosZip, { scanId, reportType });
    } catch (error) {
      logError('Failed to download scope photos zip', error, { scanId, reportType });
      throw error;
    }
  };

  const scanIsInitial = !scan?.scanType || scan?.scanType === 'initial';
  const canEditCurrentProjectWork = !featureFlags.turnBasedHandoff || scanIsEditable;

  const shouldShowPurchaseOrderExport = reportType === 'fullScope' || reportType === 'closeout' || reportType === 'changeOrder';
  const shouldShowShowChangeOrderButton = reportType === 'fullScope' && scanHasScopeChanges;

  const showButtons = !!scan && projectIsEditable;
  const showCancelButton = showButtons && canEditCurrentProjectWork && scan.scanType === 'changeRequest' && scan.scanLifecycleStatus !== 'approved';
  const showViewPunchlistButton = showButtons && scanHasTasksNeedingRework;
  const showViewCloseoutButton = showButtons && scanIsReadyForCloseout;
  const showViewChangeOrderButton = showButtons && !!viewChangeOrderScanId;
  const showViewScopeButton = showButtons && (showViewPunchlistButton || showViewCloseoutButton || showViewChangeOrderButton);

  // TODO: use effectiveScanType from useScanEditingContextSlice to determine the submit button label as soon as it's available
  const submitDoc = effectiveScanType === 'initial'
    ? 'Scope'
    : effectiveScanType === 'changeRequest'
    ? 'Change Order'
    : effectiveScanType === 'progressCompleted'
    ? 'Close Out'
    : undefined;

  const showApproveCloseOut = scanIsReviewable && scanIsReadyForCloseout && !scanHasScopeChanges;

  const progressTypeName = scanHasTasksNeedingRework ? 'Punch List' : scanIsReadyForCloseout ? 'Close Out' : 'Progress';

  const onApproveCloseOut = () => {
    if (!scan) return;
    confirm({
      title: 'Approve Closeout',
      message: `Are you ready to approve this project's closeout?`,
      onConfirm: async () => {
        await scanMutators.update(scan.id, {
          scanLifecycleStatus: 'approved',
        });
        trackEvent(ANALYTICS_EVENTS.scanApproved, { scanId: scan.id, phase: 'closeout' });

        navigateToProject({ projectId: scan.propertyProjectId });
      },
    });
  };

  const submitForReview = () => {
    if (!scan || !canEditCurrentProjectWork || !submitDoc) return;
    confirm({
      title: `Submit ${submitDoc}`,
      message: `Are you ready to submit this ${submitDoc} for review?`,
      onConfirm: async () => {
        await scanMutators.update(scan.id, {
          scanLifecycleStatus: 'pendingReview',
        });
        trackEvent(ANALYTICS_EVENTS.scanSubmittedForReview, { scanId: scan.id, documentType: submitDoc });
      },
    });
  };

  const isOutdated = scanScopeState === 'outdated';

  if (!scanRoot || !scan) return null;

  return (
    <div css={TOOLBAR_STYLE}>
      <div css={TOOLBAR_LEFT_SECTION_STYLE}>
        {!isOutdated && (
          <Button
            onClick={() => navigateToScan({ scanId: scan!.id })}
            buttonStyle='darkestGray'
            size='medium'
          >
            Scope Editor
          </Button>
        )}
        {!scanIsInitial
          && (
            <Button
              onClick={() => navigateToScan({ scanId: scan!.id, progress: true })}
              buttonStyle='darkestGray'
              size='medium'
            >
              View Progress
            </Button>
          )}

        {showViewScopeButton && (
          <Button
            onClick={() => navigateToScope({ scanId: scan?.id })}
            buttonStyle='darkestGray'
            size='medium'
            disabled={reportType === 'fullScope'}
          >
            View Scope
          </Button>
        )}
        {showViewPunchlistButton && (
          <Button
            onClick={() => navigateToPunchlistScopePreview({ scanId: scan?.id })}
            buttonStyle='darkestGray'
            size='medium'
            disabled={reportType === 'punchlist'}
          >
            View Punch List
          </Button>
        )}
        {showViewCloseoutButton && (
          <Button
            onClick={() => navigateToCloseoutScopePreview({ scanId: scan?.id })}
            buttonStyle='darkestGray'
            size='medium'
            disabled={reportType === 'closeout'}
          >
            View Closeout
          </Button>
        )}
        {showViewChangeOrderButton && (
          <Button
            onClick={() => viewChangeOrderScanId && navigateToChangeOrder({ scanId: viewChangeOrderScanId })}
            buttonStyle='darkestGray'
            size='medium'
            disabled={reportType === 'changeOrder' && viewChangeOrderScanId === scan.id}
          >
            View Change Order
          </Button>
        )}

        <Dropdown triggerContent={<DocumentDownloadIcon color='currentColor' width={20} height={20} />}>
          {shouldShowPurchaseOrderExport && (
            <Dropdown.Item onClick={prepareExcelReport} disabled={isExportingExcel} shimmer={isExportingExcel}>
              {isExportingExcel
                ? `Exporting ${documentName} (Excel)...`
                : isApproved
                ? 'Download Approved Excel'
                : 'Download Excel Preview'}
            </Dropdown.Item>
          )}
          <Dropdown.Item onClick={preparePdfReport} disabled={isExportingPdf} shimmer={isExportingPdf}>
            {isExportingPdf
              ? `Exporting ${documentName} (PDF)...`
              : isApproved
              ? 'Download Approved PDF'
              : 'Download PDF Preview'}
          </Dropdown.Item>
          <Dropdown.Item onClick={() => downloadScopePhotosZip(scan?.id)}>
            Download All {pluralize('photo', scanRoot.optimized.allPhotos.length)} (ZIP)
          </Dropdown.Item>
        </Dropdown>
      </div>
      <div css={TOOLBAR_RIGHT_SECTION_STYLE}>
        {showCancelButton && scanRoot && baselineScanRoot && (
          <Button
            onClick={() => {
              confirm({
                title: 'Cancel Change Order',
                message: 'Are you sure you want to cancel this change order? This action cannot be undone.',
                onConfirm: async () => {
                  await scanMutators.cancelChangeOrder(scan.id);
                  trackEvent(ANALYTICS_EVENTS.scanChangeOrderCanceled, { scanId: scan.id });
                  navigateToProject({ projectId: scan.propertyProjectId });
                },
              });
            }}
            buttonStyle='yellow'
            size='medium'
          >
            <CancelIcon /> CANCEL CHANGE ORDER
          </Button>
        )}
        {scanIsReviewable && (
          <Button
            onClick={() => navigateToScan({ scanId: scan.id, review: true })}
            buttonStyle={'green'}
            size='medium'
          >
            REVIEW {submitDoc}
          </Button>
        )}
        {canEditCurrentProjectWork && scanIsSubmittable && submitDoc && (
          <Button
            onClick={submitForReview}
            buttonStyle={'green'}
            size='medium'
          >
            SUBMIT {submitDoc}
          </Button>
        )}
        {showApproveCloseOut && (
          <Button
            onClick={onApproveCloseOut}
            buttonStyle='green'
            size='medium'
          >
            APPROVE {progressTypeName}
          </Button>
        )}

        {shouldShowShowChangeOrderButton && scan && (
          <Button
            onClick={() => navigateToChangeOrder({ scanId: scan?.id })}
            buttonStyle='gray'
            size='medium'
          >
            Change Order
          </Button>
        )}
      </div>
    </div>
  );
};
